import { CockpitAreaGate } from "@/components/cockpit/CockpitAreaGate";
import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { EmptyAreaNote } from "@/components/cockpit/EmptyAreaNote";
import { CockpitPageShell } from "@/components/cockpit/CockpitPageShell";
import { CockpitPageHeader } from "@/components/cockpit/CockpitPageHeader";
import { CockpitErrorNotice } from "@/components/cockpit/CockpitErrorNotice";
import { CockpitSkeletonRows } from "@/components/cockpit/CockpitSkeleton";
import { CockpitSearchField } from "@/components/cockpit/CockpitSearchField";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Download, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  listMyInvoices,
  getMyInvoiceSignedUrl,
  type CustomerInvoiceItem,
} from "@/lib/invoices.functions";
import { InvoiceStatusBadge } from "@/components/invoices/InvoiceStatusBadge";
import {
  invoiceKindLabel,
  invoiceStatusNote,
  invoiceYears,
  prepareInvoices,
  type InvoiceSort,
} from "@/lib/cockpit-invoices";

export const Route = createFileRoute("/_cockpit/cockpit/rechnungen")({
  head: () => ({
    meta: [
      { title: "Rechnungen – Cockpit – creategy" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: () => (
    <CockpitAreaGate area="invoices">
      <CockpitInvoicesPage />
    </CockpitAreaGate>
  ),
});

const dateFmt = new Intl.DateTimeFormat("de-DE", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
});

function formatAmount(cents: number, currency: string): string {
  try {
    return new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: currency || "EUR",
    }).format(cents / 100);
  } catch {
    return `${(cents / 100).toFixed(2)} ${currency}`;
  }
}

const SORT_LABELS: Record<InvoiceSort, string> = {
  date_desc: "Datum, neueste zuerst",
  date_asc: "Datum, älteste zuerst",
  amount_desc: "Betrag, absteigend",
  amount_asc: "Betrag, aufsteigend",
};

const selectClass =
  "h-11 rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

function CockpitInvoicesPage() {
  const listFn = useServerFn(listMyInvoices);
  const signFn = useServerFn(getMyInvoiceSignedUrl);
  const [search, setSearch] = useState("");
  const [year, setYear] = useState<number | null>(null);
  const [sort, setSort] = useState<InvoiceSort>("date_desc");
  const [busy, setBusy] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ["cockpit-invoices"],
    queryFn: () => listFn(),
    staleTime: 30_000,
  });

  const years = useMemo(() => invoiceYears(data ?? []), [data]);
  // Eine gemeinsame Grundlage für Tabelle und Kartenliste.
  const rows = useMemo(
    () => prepareInvoices(data ?? [], { search, year, sort }),
    [data, search, year, sort],
  );

  async function onDownload(item: CustomerInvoiceItem) {
    setBusy(item.id);
    try {
      const res = await signFn({ data: { id: item.id } });
      const a = document.createElement("a");
      a.href = res.url;
      a.download = res.file_name;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Download fehlgeschlagen");
    } finally {
      setBusy(null);
    }
  }

  const totalCount = data?.length ?? 0;

  return (
    <CockpitPageShell>
      <CockpitPageHeader
        title="Rechnungen"
        description="Ihre Rechnungen von creategy – mit direktem Download als PDF."
        actions={
          totalCount > 0 ? (
            <CockpitSearchField
              value={search}
              onChange={setSearch}
              placeholder="Rechnungsnummer"
              label="Rechnungen durchsuchen"
              resultCount={rows.length}
              resultNoun={{ one: "Treffer", many: "Treffer" }}
            />
          ) : undefined
        }
      />

      <CockpitErrorNotice error={error} title="Rechnungen konnten nicht geladen werden" />

      {isLoading ? (
        <CockpitSkeletonRows />
      ) : totalCount === 0 ? (
        /* Ticket KN3: einheitlicher Leerzustand. */
        <EmptyAreaNote
          title="Hier erscheinen Ihre Rechnungen"
          reason="Für Ihr Konto liegt bisher keine Rechnung vor. Sobald wir eine Rechnung hinterlegen, finden Sie sie hier zum Download."
          icon={<FileText className="h-8 w-8" aria-hidden />}
        />
      ) : (
        <>
          <div className="flex flex-wrap items-end gap-3">
            {years.length > 1 ? (
              <label className="text-xs font-semibold text-muted-foreground">
                <span className="block pb-1">Jahr</span>
                <select
                  value={year ?? "all"}
                  onChange={(e) =>
                    setYear(e.target.value === "all" ? null : Number(e.target.value))
                  }
                  className={selectClass}
                >
                  <option value="all">Alle Jahre</option>
                  {years.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
            <label className="text-xs font-semibold text-muted-foreground">
              <span className="block pb-1">Sortierung</span>
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value as InvoiceSort)}
                className={selectClass}
              >
                {(Object.keys(SORT_LABELS) as InvoiceSort[]).map((key) => (
                  <option key={key} value={key}>
                    {SORT_LABELS[key]}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {rows.length === 0 ? (
            <p className="mt-8 text-sm text-muted-foreground">
              Keine Rechnung passt zu Ihrer Auswahl.
            </p>
          ) : (
            <>
              {/* Breite Bildschirme: Tabelle */}
              <div className="mt-6 hidden overflow-hidden rounded-md border border-border bg-card lg:block">
                <table className="w-full text-sm">
                  <caption className="sr-only">
                    Ihre Rechnungen mit Datum, Betrag, Status und Download
                  </caption>
                  <thead className="bg-secondary/60 text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <tr>
                      <th scope="col" className="px-4 py-3 font-semibold">
                        Rechnungsnummer
                      </th>
                      <th scope="col" className="px-4 py-3 font-semibold">
                        Datum
                      </th>
                      <th scope="col" className="px-4 py-3 text-right font-semibold">
                        Betrag brutto
                      </th>
                      <th scope="col" className="px-4 py-3 font-semibold">
                        Status
                      </th>
                      <th scope="col" className="px-4 py-3 text-right font-semibold">
                        <span className="sr-only">Download</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {rows.map((r) => (
                      <tr key={r.id} className="align-middle">
                        <td className="px-4 py-3 font-semibold text-primary">
                          {r.invoice_number}
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">
                          {dateFmt.format(new Date(r.invoice_date))}
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums text-foreground">
                          {formatAmount(r.amount_gross_cents, r.currency)}
                          {invoiceKindLabel(r) && (
                            <span className="ml-2 whitespace-nowrap text-xs font-semibold text-muted-foreground">
                              {invoiceKindLabel(r)}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {invoiceStatusNote(r) ? (
                            <span className="text-xs text-muted-foreground">
                              {invoiceStatusNote(r)}
                            </span>
                          ) : (
                            <InvoiceStatusBadge status={r.status} />
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button
                            type="button"
                            onClick={() => onDownload(r)}
                            disabled={busy === r.id}
                            aria-label={`Rechnung ${r.invoice_number} als PDF herunterladen`}
                            className="inline-flex min-h-11 items-center gap-2 rounded-md bg-brand-button px-3 py-1.5 text-sm font-semibold text-brand-button-foreground disabled:opacity-60"
                          >
                            {busy === r.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                            ) : (
                              <Download className="h-4 w-4" aria-hidden />
                            )}
                            PDF
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Schmale Bildschirme: Kartenstapel */}
              <ul className="mt-6 space-y-3 lg:hidden">
                {rows.map((r) => (
                  <li key={r.id} className="rounded-md border border-border bg-card p-4">
                    <div className="flex min-w-0 items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="break-words font-display text-base font-extrabold text-primary">
                          {r.invoice_number}
                        </p>
                        <p className="mt-1 text-xs text-muted-foreground">
                          {dateFmt.format(new Date(r.invoice_date))}
                        </p>
                      </div>
                      <p className="tabular-nums text-sm font-semibold text-foreground">
                        {formatAmount(r.amount_gross_cents, r.currency)}
                        {invoiceKindLabel(r) && (
                          <span className="mt-0.5 block text-xs font-semibold text-muted-foreground">
                            {invoiceKindLabel(r)}
                          </span>
                        )}
                      </p>
                    </div>
                    <div className="mt-2">
                      {invoiceStatusNote(r) ? (
                        <span className="text-xs text-muted-foreground">
                          {invoiceStatusNote(r)}
                        </span>
                      ) : (
                        <InvoiceStatusBadge status={r.status} />
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => onDownload(r)}
                      disabled={busy === r.id}
                      className="mt-3 inline-flex min-h-11 items-center gap-2 rounded-md bg-brand-button px-4 py-2 text-sm font-semibold text-brand-button-foreground disabled:opacity-60"
                    >
                      {busy === r.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                      ) : (
                        <Download className="h-4 w-4" aria-hidden />
                      )}
                      PDF herunterladen
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}
        </>
      )}
    </CockpitPageShell>
  );
}
